import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { PSelectItem } from '../../../../../shared/design-system/atoms/p-select/p-select-item.model';
import { Report } from '../../../../models/report.model';
import { getExposureLabel } from '../../../../services/mappers/exposition.mapper';

@Component({
  selector: 'app-appartement-form',
  templateUrl: './appartement-form.component.html',
  styleUrls: ['./appartement-form.component.scss'],
})
export class AppartementFormComponent implements OnChanges {
  @Input() appartement!: Report.Characteristics.Appartement | undefined;
  @Input() constructionYear!: number;

  @Output() appartementChanged =
    new EventEmitter<Report.Characteristics.Appartement>();

  @Output() constructionYearChanged = new EventEmitter<number>();

  appartementForm = new FormGroup({
    propertyType: new FormControl('Appartement', { nonNullable: true }),
    constructionYear: new FormControl('', [
      Validators.min(1000),
      Validators.max(9999),
      Validators.pattern(/^\d{4}$/),
    ]),
    livingAreaM2: new FormControl(''),
    livingRoomAreaM2: new FormControl(''),
    heating: new FormControl(''),
    floor: new FormControl(''),
  });

  numberOfRoomsItem!: PSelectItem;
  numberOfBedroomsItem!: PSelectItem;
  numberOfBathroomsItem!: PSelectItem;
  exposureItem!: PSelectItem;
  ghgEmissionsItem!: PSelectItem;
  energyPerformanceItem!: PSelectItem;

  numberSelectItems: PSelectItem[] = [
    { value: null, label: '' },
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4' },
    { value: '5', label: '5' },
    { value: '6', label: '6' },
    { value: '7', label: '7' },
    { value: '8', label: '8' },
    { value: '9', label: '9' },
    { value: '10 et +', label: '10 et +' },
  ];

  exposureSelectItems: PSelectItem[] = [
    { value: null, label: '' },
    { value: 'N', label: 'Nord' },
    { value: 'S', label: 'Sud' },
    { value: 'E', label: 'Est' },
    { value: 'O', label: 'Ouest' },
    { value: 'NE', label: 'Nord-Est' },
    { value: 'SE', label: 'Sud-Est' },
    { value: 'NO', label: 'Nord-Ouest' },
    { value: 'SO', label: 'Sud-Ouest' },
  ];

  alphabiticalCategoriesItems: PSelectItem[] = [
    { value: null, label: '' },
    { value: 'A', label: 'A' },
    { value: 'B', label: 'B' },
    { value: 'C', label: 'C' },
    { value: 'D', label: 'D' },
    { value: 'E', label: 'E' },
    { value: 'F', label: 'F' },
    { value: 'G', label: 'G' },
  ];

  appartementInternal!: Report.Characteristics.Appartement;
  constructionYearinternal!: number;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['appartement'] && changes['appartement'].currentValue) {
      this.appartementInternal = changes['appartement'].currentValue;
    }

    if (
      changes['constructionYear'] &&
      changes['constructionYear'].currentValue
    ) {
      this.appartementForm.controls.constructionYear.setValue(
        this.constructionYear.toString(),
      );
    }

    this.setFormValues();
  }

  onConstructionYearChanged(event: Event) {
    this.constructionYearinternal = parseInt(
      (event.target as HTMLInputElement).value,
    );

    this.constructionYearChanged.emit(this.constructionYearinternal);
  }

  onLivingAreaM2Changed(event: Event) {
    this.appartementInternal = {
      ...this.appartementInternal,
      livingAreaM2: parseInt((event.target as HTMLInputElement).value),
    };

    this.appartementChanged.emit(this.appartementInternal);
  }

  onLivingRoomAreaM2Changed(event: Event) {
    this.appartementInternal = {
      ...this.appartementInternal,
      livingRoomAreaM2: parseInt((event.target as HTMLInputElement).value),
    };

    this.appartementChanged.emit(this.appartementInternal);
  }

  onHeatingChanged(event: Event) {
    this.appartementInternal = {
      ...this.appartementInternal,
      heating: (event.target as HTMLInputElement).value,
    };

    this.appartementChanged.emit(this.appartementInternal);
  }

  onFloorChanged(event: Event) {
    this.appartementInternal = {
      ...this.appartementInternal,
      floor: (event.target as HTMLInputElement).value,
    };

    this.appartementChanged.emit(this.appartementInternal);
  }

  onExposureSelected({ value }: PSelectItem): void {
    this.appartementInternal = {
      ...this.appartementInternal,
      exposure: value ? value : null,
    };

    this.exposureItem = { value };

    this.appartementChanged.emit(this.appartementInternal);
  }

  onNumberOfRoomsSelected({ value }: PSelectItem): void {
    this.appartementInternal = {
      ...this.appartementInternal,
      numberOfRooms: value ? value : null,
    };

    this.numberOfRoomsItem = { value };

    this.appartementChanged.emit(this.appartementInternal);
  }

  onNumberOfBedroomsSelected({ value }: PSelectItem): void {
    this.appartementInternal = {
      ...this.appartementInternal,
      numberOfBedrooms: value ? value : null,
    };

    this.numberOfBedroomsItem = { value };

    this.appartementChanged.emit(this.appartementInternal);
  }

  onNumberOfBathroomsSelected({ value }: PSelectItem): void {
    this.appartementInternal = {
      ...this.appartementInternal,
      numberOfBathrooms: value ? value : null,
    };

    this.numberOfBathroomsItem = { value };

    this.appartementChanged.emit(this.appartementInternal);
  }

  onGhgEmissionsSelected({ value }: PSelectItem): void {
    this.appartementInternal = {
      ...this.appartementInternal,
      ghgEmissions: value ? value : null,
    };

    this.ghgEmissionsItem = { value };

    this.appartementChanged.emit(this.appartementInternal);
  }

  onEnergyPerformanceSelected({ value }: PSelectItem): void {
    this.appartementInternal = {
      ...this.appartementInternal,
      energyPerformance: value ? value : null,
    };

    this.energyPerformanceItem = { value };

    this.appartementChanged.emit(this.appartementInternal);
  }

  setFormValues(): void {
    this.appartementForm.controls.constructionYear.setValue(
      this.constructionYearinternal
        ? this.constructionYearinternal.toString()
        : '',
    );

    this.appartementForm.controls.livingAreaM2.setValue(
      this.appartementInternal?.livingAreaM2?.toString() || '',
    );

    this.appartementForm.controls.livingRoomAreaM2.setValue(
      this.appartementInternal?.livingRoomAreaM2?.toString() || '',
    );

    this.appartementForm.controls.heating.setValue(
      this.appartementInternal?.heating || '',
    );

    this.appartementForm.controls.floor.setValue(
      this.appartementInternal?.floor || '',
    );

    this.numberOfRoomsItem = {
      value:
        this.appartementInternal?.numberOfRooms ||
        this.numberSelectItems[0].value,
    };

    this.numberOfBedroomsItem = {
      value:
        this.appartementInternal?.numberOfBedrooms ||
        this.numberSelectItems[0].value,
    };

    this.numberOfBathroomsItem = {
      value:
        this.appartementInternal?.numberOfBathrooms ||
        this.numberSelectItems[0].value,
    };

    this.exposureItem = {
      value:
        this.appartementInternal?.exposure || this.exposureSelectItems[0].value,
      label:
        getExposureLabel(this.appartementInternal?.exposure) ||
        this.exposureSelectItems[0].label,
    };

    this.ghgEmissionsItem = {
      value:
        this.appartementInternal?.ghgEmissions ||
        this.alphabiticalCategoriesItems[0].value,
    };

    this.energyPerformanceItem = {
      value:
        this.appartementInternal?.energyPerformance ||
        this.alphabiticalCategoriesItems[0].value,
    };

    if (!this.appartementInternal.numberOfRooms) {
      this.appartementInternal.numberOfRooms = this.numberSelectItems[0]
        .value as string;
    }

    if (!this.appartementInternal.numberOfBedrooms) {
      this.appartementInternal.numberOfBedrooms = this.numberSelectItems[0]
        .value as string;
    }

    if (!this.appartementInternal.numberOfBathrooms) {
      this.appartementInternal.numberOfBathrooms = this.numberSelectItems[0]
        .value as string;
    }

    if (!this.appartementInternal.exposure) {
      this.appartementInternal.exposure = this.exposureSelectItems[0]
        .value as string;
    }

    if (!this.appartementInternal.ghgEmissions) {
      this.appartementInternal.ghgEmissions = this
        .alphabiticalCategoriesItems[0].value as string;
    }

    if (!this.appartementInternal.energyPerformance) {
      this.appartementInternal.energyPerformance = this
        .alphabiticalCategoriesItems[0].value as string;
    }
  }
}
